import DraftRecoveryChoices from "../Components/DraftRecoveryChoices";
import useRecoverableAutosave from "../hooks/useRecoverableAutosave";
import DraftSaveStatus from "../Components/DraftSaveStatus";
import Page from "../Components/Page";
import MainButton from "../Components/Buttons/MainButton";
import FormTextField from "../Components/FormInputElements/FormTextField";
import FormSelectField from "../Components/FormInputElements/FormSelectField";
import Divider from "@mui/material/Divider";
import MeasuredSoilHorizonInput from "../Components/MeasuredSoilHorizonInput";

import BooleanQuestion from "../Components/FormInputElements/BooleanQuestion";
import { useNavigate, useParams } from "react-router-dom";

import MapComponent from "../Components/MapComponent";
import FieldBoundaryImport from "../Components/FieldBoundaryImport";

import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button } from "@mui/material";
import { formDraftKey, readFormDraftAsync, clearFormDraft, newEditorDraftKey } from "../utils/formDrafts";
import { useSelectedAggregatorGrower } from "../Components/AggregatorSelectedGrowerPanel";
import {
  axiosInstance,
  buildLegacyHeaders,
  fetchFieldBoundariesSummary,
  fetchFarm,
  fetchField,
  fetchFieldSoilEnrichment,
  normalizeFieldShape,
  refreshFieldSoilEnrichment,
  resolveFieldSoil,
  validateFieldBoundaries,
} from "../Components/axiosFetchers";
import { geocodeAddress } from "../Components/googleMapsGeocoder";
import {
  extractResolvedSoilFieldValues,
  mergeBlankResolvedSoilFieldValues,
} from "../Components/resolvedSoilFieldValues";
import {
  calculateBoundaryCentroid,
  hasAuthoritativeFieldBoundaries,
} from "../utils/fieldMapAuthority";
import {
  buildCoordinateRegionDescription,
  isCoordinateInCanadianFarmRegion,
} from "../utils/canadianCoordinates";

import { useAuth } from "../Components/Auth/useAuth";
import FieldBoundaryValidationNotice from "../Components/FieldBoundaryValidationNotice";
import FieldSoilEstimatePanel from "../Components/FieldSoilEstimatePanel";
import WarningMessageModal from "../Components/WarningMessageModal";
import {
  buildFieldBoundaryValidationRequest,
  createBoundaryValidationState,
  normalizeBoundaryValidationError,
  normalizeBoundaryValidationResult,
} from "../Components/fieldBoundaryValidationState";
import VersionHistoryControl, {
  HistoryViewingBanner,
} from "../Components/VersionHistoryControl";
import { FIELD_CREATOR } from "../Assets/contextFactories_V0";
import { useClientConfig } from "../hooks/useClientConfig";
import { useReferenceData } from "../hooks/useReferenceData";
import useVersionHistory from "../hooks/useVersionHistory";
import { buildSparsePayload } from "../utils/sparsePayload";
import { hasComparableFormChanges } from "../utils/formComparison";
import { trackEvent } from "../analytics/ga4.js";
import { duplicateNameMessage, isBoundaryOverlapError, isConflictError } from "../utils/apiErrors.js";
import { buildFieldValidationErrors } from "../utils/fieldFormValidation.js";
import {
  formatAcres,
  formatAcresForInput,
} from "../utils/acreageFormatting.js";

const EMPTY_FIELD_ERRORS = {
  name: false,
  fieldSize: false,
  fieldAddress: false,
  latitude: false,
  longitude: false,
  surfaceForm: false,
  slopeClass: false,
  soilType: false,
  surfaceSoilTexture: false,
  topsoilHorizonThickness: false,
};

const FIELD_ERROR_KEYS = {
  name: "name",
  field_size: "fieldSize",
  field_address: "fieldAddress",
  latitude: "latitude",
  longitude: "longitude",
  surface_form: "surfaceForm",
  slope_class: "slopeClass",
  soil_type: "soilType",
  surface_soil_texture: "surfaceSoilTexture",
  topsoil_horizon_thickness_cm: "topsoilHorizonThickness",
};

function buildFieldPayload(field) {
  return { ...buildSparsePayload({
    latitude: field?.latitude === "" || field?.latitude == null ? null : Number(field.latitude),
    longitude: field?.longitude === "" || field?.longitude == null ? null : Number(field.longitude),
    name: field?.name,
    field_size: Number(field?.field_size),
    field_address: field?.field_address,
    surface_form: field?.surface_form,
    slope_class: field?.slope_class,
    soil_type: field?.soil_type,
    soil_code: field?.soil_code,
    surface_soil_texture: field?.surface_soil_texture,
    topsoil_horizon_thickness_cm: field?.topsoil_horizon_thickness_cm === "" || field?.topsoil_horizon_thickness_cm == null ? null : Number(field.topsoil_horizon_thickness_cm),
    ecodistrict: String(field?.ecodistrict || ""),
    slc_polygon: String(field?.slc_polygon || ""),
    field_boundaries_size: field?.field_boundaries_size,
    has_boundaries: field?.has_boundaries,
    boundaries: field?.boundaries,
  }), boundaries: field?.boundaries || [] };
}

export default function FieldFormPage() {
  const { user, impersonatedEmail } = useAuth();
  const boundaryImportInputRef = useRef(null);
  const userRef = useRef(user);
  userRef.current = user;
  const selectedGrower = useSelectedAggregatorGrower();
  const [addressQuery, setAddressQuery] = useState("Canada");
  const [mapSearchLocation, setMapSearchLocation] = useState(null);
  const [mapSearchError, setMapSearchError] = useState("");
  const navigate = useNavigate();
  const { farmId, fieldId } = useParams();
  const isEditing = Boolean(fieldId);
  const baseDraftKey = formDraftKey({ user, impersonatedEmail, growerPublicId: selectedGrower?.grower_profile_public_id }, `field:${farmId}:${fieldId || "new"}`);
  const draftKey = useMemo(() => isEditing ? baseDraftKey : newEditorDraftKey(baseDraftKey), [baseDraftKey, isEditing]);
  const [loadedDraftKey, setLoadedDraftKey] = useState("");
  const [recoverableDraft, setRecoverableDraft] = useState(null);
  const [isTypingAddress, setIsTypingAddress] = useState(false);

  const [field, setField] = useState(FIELD_CREATOR());
  const [savedField, setSavedField] = useState(FIELD_CREATOR());
  const [farm, setFarm] = useState(null);
  const [referenceFarms, setReferenceFarms] = useState([]);
  const [boundaryToolbarReady, setBoundaryToolbarReady] = useState(false);
  const [boundaryValidation, setBoundaryValidation] = useState(
    createBoundaryValidationState()
  );
  const [saveError, setSaveError] = useState("");
  const [errorFields, setErrorFields] = useState(EMPTY_FIELD_ERRORS);
  const [isSaving, setIsSaving] = useState(false);
  const [soilEnrichmentState, setSoilEnrichmentState] = useState(null);
  const [coordinateSoilResolution, setCoordinateSoilResolution] = useState(null);
  const [soilEnrichmentLoading, setSoilEnrichmentLoading] = useState(false);
  const [soilEnrichmentError, setSoilEnrichmentError] = useState("");
  const { config } = useClientConfig();
  const [boundaryLocationWarningOpen, setBoundaryLocationWarningOpen] =
    useState(false);
  const { referenceData } = useReferenceData([
    "surface_forms",
    "slope_classes",
    "soil_types",
    "surface_soil_textures",
  ]);
  const history = useVersionHistory({
    user,
    enabled: isEditing && Boolean(fieldId),
    historyPath:
      farmId && fieldId ? `/farms/${farmId}/fields/${fieldId}` : "",
    currentData: field,
    normalizeDetail: (detail) => ({
      ...FIELD_CREATOR(),
      ...(detail || {}),
      boundaries: Array.isArray(detail?.boundaries) ? detail.boundaries : [],
      has_boundaries:
        detail?.has_boundaries ??
        Boolean(Array.isArray(detail?.boundaries) && detail.boundaries.length),
    }),
    onRestore: async (snapshot) => {
      const response = await axiosInstance.patch(
        `${process.env.REACT_APP_API_URL}/farms/${farmId}/fields/${fieldId}`,
        buildSparsePayload({
          name: snapshot.name,
          field_size: Number(snapshot.field_size),
          field_address: snapshot.field_address,
          surface_form: snapshot.surface_form,
          slope_class: snapshot.slope_class,
          soil_type: snapshot.soil_type,
          soil_code: snapshot.soil_code,
          surface_soil_texture: snapshot.surface_soil_texture,
          topsoil_horizon_thickness_cm: snapshot.topsoil_horizon_thickness_cm ?? null,
          tillage_regime: snapshot.tillage_regime,
          previous_tillage_regime: snapshot.previous_tillage_regime,
          regime_change_date: snapshot.regime_change_date,
          ecodistrict: String(snapshot.ecodistrict || ""),
          slc_polygon: String(snapshot.slc_polygon || ""),
          field_boundaries_size: snapshot.field_boundaries_size,
          has_boundaries: snapshot.has_boundaries,
          boundaries: snapshot.boundaries,
        }),
        { headers: buildLegacyHeaders(user) }
      );

      return {
        ...FIELD_CREATOR(),
        ...(response.data || {}),
        boundaries: Array.isArray(response.data?.boundaries)
          ? response.data.boundaries
          : [],
        has_boundaries:
          response.data?.has_boundaries ??
          Boolean(
            Array.isArray(response.data?.boundaries) &&
              response.data.boundaries.length
          ),
      };
    },
    onRestoreSuccess: (savedField) => {
      setField(savedField);
      setSavedField(savedField);
    },
  });
  const displayedField = history.versionData || field;
  const isViewingHistoricalVersion = history.isViewingHistoricalVersion;
  const currentBoundariesAreAuthoritative =
    hasAuthoritativeFieldBoundaries(field);
  const displayedBoundariesAreAuthoritative =
    hasAuthoritativeFieldBoundaries(displayedField);

  document.title = `${isEditing ? "Edit" : "Add"} Field Page - Field To Market Canada`;

  useEffect(() => {
    let isMounted = true;
    setLoadedDraftKey("");
    setField(FIELD_CREATOR());
    setSavedField(FIELD_CREATOR());
    setMapSearchLocation(null);
    setErrorFields(EMPTY_FIELD_ERRORS);
    setSaveError("");

    async function startup() {
      const [nextFarm, nextReferenceFarms, nextField, nextSoilEnrichment] = await Promise.all([
        fetchFarm(farmId, userRef.current),
        fetchFieldBoundariesSummary(userRef.current).catch(() => []),
        isEditing ? fetchField(farmId, fieldId, userRef.current) : Promise.resolve(FIELD_CREATOR()),
        isEditing
          ? fetchFieldSoilEnrichment(farmId, fieldId, userRef.current).catch(() => null)
          : Promise.resolve(null),
      ]);

      if (!isMounted) {
        return;
      }

      setFarm(nextFarm);
      setReferenceFarms(nextReferenceFarms);
      setField(nextField);
      setSavedField(normalizeFieldShape(nextField));
      setSoilEnrichmentState(nextSoilEnrichment);
      const localRecovery = await readFormDraftAsync(draftKey);
      if (!isMounted) return;
      setRecoverableDraft(localRecovery);
      setLoadedDraftKey(draftKey);
    }

    startup().catch(() => {
      if (isMounted) setSaveError("We couldn't load this field. Reload the page to try again.");
    });

    return () => {
      isMounted = false;
    };
  }, [farmId, fieldId, isEditing, draftKey]);

  useEffect(() => {
    if (loadedDraftKey !== draftKey || recoverableDraft) return;
    const dirty = hasComparableFormChanges(buildFieldPayload(field), buildFieldPayload(savedField));
    const beforeUnload = (event) => { if (dirty) { event.preventDefault(); event.returnValue = ""; } };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [draftKey, loadedDraftKey, field, savedField, recoverableDraft]);

  const autosave = useRecoverableAutosave({ draftKey, value: field,
    dirty: hasComparableFormChanges(field, savedField), versionId: savedField.current_version_id,
    target: { kind: "field", farm_id: Number(farmId), field_id: null, resource_id: field.id || (fieldId ? Number(fieldId) : null) },
    enabled: loadedDraftKey === draftKey && !isViewingHistoricalVersion, suspendSync: Boolean(recoverableDraft),
    onRecover: setRecoverableDraft });

  useEffect(() => {
    setAddressQuery(displayedField?.field_address || "Canada");
  }, [displayedField?.field_address]);

  useEffect(() => {
    if (!currentBoundariesAreAuthoritative) {
      return;
    }

    setMapSearchLocation(null);
    setMapSearchError("");
    setCoordinateSoilResolution(null);
    setIsTypingAddress(false);
    setErrorFields((current) => ({
      ...current,
      fieldAddress: false,
      surfaceForm: false,
      slopeClass: false,
      soilType: false,
      surfaceSoilTexture: false,
    }));
  }, [currentBoundariesAreAuthoritative]);

  useEffect(() => {
    let isMounted = true;

    async function runBoundaryValidation() {
      try {
        setBoundaryValidation(
          createBoundaryValidationState({
            status: "loading",
          })
        );

        const payload = buildFieldBoundaryValidationRequest(farmId, {
          id: field.id,
          boundaries: field.boundaries,
        });

        const result = await validateFieldBoundaries(payload, user);

        if (isMounted) {
          setBoundaryValidation(normalizeBoundaryValidationResult(result));
        }
      } catch (error) {
        if (isMounted) {
          setBoundaryValidation(normalizeBoundaryValidationError(error));
        }
      }
    }

    if (isSaving) {
      return () => {
        isMounted = false;
      };
    }

    if (!field.has_boundaries || field.boundaries.length < 3) {
      setBoundaryValidation(createBoundaryValidationState());
      return () => {
        isMounted = false;
      };
    }

    const timeout = window.setTimeout(runBoundaryValidation, 300);

    return () => {
      isMounted = false;
      window.clearTimeout(timeout);
    };
  }, [farmId, field.boundaries, field.has_boundaries, field.id, isSaving, user]);

  function handleStateChange(target, key) {
    setField((prevState) => ({ ...prevState, [key]: target }));
    const errorKey = FIELD_ERROR_KEYS[key];
    if (errorKey) {
      setErrorFields((current) => ({ ...current, [errorKey]: false }));
    }
    setSaveError("");
  }

  async function handleAddressChange(address) {
    setIsTypingAddress(false);

    if (currentBoundariesAreAuthoritative) {
      setMapSearchLocation(null);
      setMapSearchError("");
      return;
    }

    setAddressQuery(address);

    if (!address) return;
    let coordinates;
    try {
      coordinates = await geocodeAddress(address);
      if (!isCoordinateInCanadianFarmRegion(coordinates, farm?.province)) {
        setMapSearchLocation(null);
        setCoordinateSoilResolution(null);
        setBoundaryLocationWarningOpen(true);
        setMapSearchError(
          `That search result appears outside ${buildCoordinateRegionDescription(
            farm?.province
          )}. Add a province, postal code, or nearby town in Canada and try again.`
        );
        return;
      }
      setMapSearchLocation({
        ...coordinates,
        requestId: Date.now(),
      });
      setMapSearchError("");
    } catch (error) {
      setMapSearchError("We could not find that address. Check it and try again.");
      return;
    }

    try {
      const soilData = await resolveFieldSoil(
        {
          lat: coordinates.lat,
          lng: coordinates.lng,
          province: farm?.province,
        },
        user
      );
      setCoordinateSoilResolution(soilData);
      setSoilEnrichmentError("");
    } catch (error) {
      setSoilEnrichmentError("Official soil sources could not be reached for this address.");
    }
  }

  function resolveSoilSuggestionsForField(targetField, normalized) {
    const newValues = extractResolvedSoilFieldValues(normalized, {
      soilTypeOptions: referenceData.soil_types || [],
      surfaceSoilTextureOptions: referenceData.surface_soil_textures || [],
      surfaceFormOptions: referenceData.surface_forms || [],
      slopeClassOptions: referenceData.slope_classes || [],
    });
    return mergeBlankResolvedSoilFieldValues(targetField, newValues);
  }

  function applyResolvedSoilSuggestions(normalized) {
    setField((prevState) =>
      resolveSoilSuggestionsForField(prevState, normalized)
    );
    setSavedField((prevState) =>
      resolveSoilSuggestionsForField(prevState, normalized)
    );
    setErrorFields((current) => ({
      ...current,
      surfaceForm: false,
      slopeClass: false,
      soilType: false,
      surfaceSoilTexture: false,
    }));
    setSaveError("");
  }

  async function persistFieldRecord(nextField) {
    const payload = buildFieldPayload(nextField);
    return autosave.commit(payload, {}, nextField);
  }

  async function handleSaveBoundaries(boundaryChanges) {
    const nextField = { ...field, ...boundaryChanges };
    setField(nextField);
    setSaveError("");
    setSoilEnrichmentError("");

    const boundaryCentroid = calculateBoundaryCentroid(nextField.boundaries);
    if (
      !boundaryCentroid ||
      !isCoordinateInCanadianFarmRegion(boundaryCentroid, farm?.province)
    ) {
      setBoundaryLocationWarningOpen(true);
      return;
    }

    const nextErrors = buildFieldValidationErrors(nextField);
    setErrorFields(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) {
      setSaveError(
        "Complete the required field details, then save the boundaries again to generate the official soil estimate."
      );
      return;
    }

    let persistedField;
    try {
      setIsSaving(true);
      const response = await persistFieldRecord(nextField);
      persistedField = normalizeFieldShape(response.data);
      if (response.hasNewerEdits) { setSavedField(persistedField); setField(current => ({ ...current, id: persistedField.id })); setSaveError("Boundaries saved; newer edits remain in your recoverable draft."); return; }
      setField(persistedField);
      setSavedField(persistedField);
      trackEvent("field_saved", {
        workflow_area: "field_boundary",
        is_new_record: !isEditing,
        result: "success",
      });
    } catch (error) {
      if (isBoundaryOverlapError(error)) {
        setBoundaryValidation(normalizeBoundaryValidationError(error));
        setSaveError("These boundaries overlap another saved field. Adjust the boundary or its exclusions and retry.");
      } else if (isConflictError(error)) {
        setSaveError(
          duplicateNameMessage(error, {
            resourceLabel: "field",
            name: nextField.name,
            scope: "on this farm",
          })
        );
      } else {
        setBoundaryValidation(normalizeBoundaryValidationError(error));
      }
      return;
    } finally {
      setIsSaving(false);
    }

    try {
      setSoilEnrichmentLoading(true);
      const enrichment = await refreshFieldSoilEnrichment(
        farmId,
        persistedField.id,
        user
      );
      const normalized = enrichment.normalized_data || enrichment;
      setSoilEnrichmentState({ available: true, enrichment });
      setCoordinateSoilResolution(null);
      setField((current) =>
        resolveSoilSuggestionsForField(current, normalized)
      );
      setSavedField((current) =>
        resolveSoilSuggestionsForField(current, normalized)
      );
    } catch (error) {
      setSoilEnrichmentError(
        error?.response?.data?.detail ||
          "The field and boundaries were saved, but official soil sources could not be refreshed."
      );
    } finally {
      setSoilEnrichmentLoading(false);
      if (!isEditing) {
        navigate(`/farm/${farmId}/field/${persistedField.id}/form`, {
          replace: true,
        });
      }
    }
  }

  async function handleRefreshSoilEnrichment() {
    if (!isEditing || !fieldId) return;
    setSoilEnrichmentLoading(true);
    setSoilEnrichmentError("");
    try {
      const enrichment = await refreshFieldSoilEnrichment(farmId, fieldId, user);
      setSoilEnrichmentState({ available: true, enrichment });
      setCoordinateSoilResolution(null);
      applyResolvedSoilSuggestions(enrichment.normalized_data || enrichment);
    } catch (error) {
      setSoilEnrichmentError(
        error?.response?.data?.detail || "Official soil sources could not be refreshed."
      );
    } finally {
      setSoilEnrichmentLoading(false);
    }
  }

  function isInputValid() {
    const nextErrors = buildFieldValidationErrors(field);
    setErrorFields(nextErrors);
    return !Object.values(nextErrors).some(Boolean);
  }

  async function handleSaveField() {
    if (!isInputValid()) return;
    const location = hasAuthoritativeFieldBoundaries(field)
      ? calculateBoundaryCentroid(field.boundaries)
      : { lat: Number(field.latitude), lng: Number(field.longitude) };
    const hasPoint = field.latitude !== "" && field.latitude != null && field.longitude !== "" && field.longitude != null;
    if ((hasAuthoritativeFieldBoundaries(field) || hasPoint) && !isCoordinateInCanadianFarmRegion(location, farm?.province)) {
      setBoundaryLocationWarningOpen(true);
      return;
    }

    const payload = buildFieldPayload(field);
    const boundaryChanged = hasComparableFormChanges(
      { boundaries: payload.boundaries },
      { boundaries: buildFieldPayload(savedField).boundaries }
    );

    try {
      setIsSaving(true);
      if (isEditing || field.id) {
        const response = await autosave.commit(payload);
        if (response.hasNewerEdits) { setSavedField(normalizeFieldShape(response.data)); return; }
        trackEvent("field_saved", {
          workflow_area: "field",
          is_new_record: false,
          result: "success",
        });
        if (hasAuthoritativeFieldBoundaries(field) && (
          boundaryChanged ||
          !soilEnrichmentState?.available ||
          soilEnrichmentState?.enrichment?.is_stale
        )) {
          void refreshFieldSoilEnrichment(farmId, response.data.id, user).catch(
            () => null
          );
        }
        clearFormDraft(draftKey);
        navigate(`/farm/${response.data.farm_id}/field/${response.data.id}`);
        return;
      }

      const response = await autosave.commit(payload);
      if (response.hasNewerEdits) { setSavedField(normalizeFieldShape(response.data)); setField(current => ({ ...current, id: response.data.id })); setSaveError("Field saved. Newer edits remain in your recoverable draft; reopen the saved field to continue."); return; }
      trackEvent("field_saved", {
        workflow_area: "field",
        is_new_record: true,
        result: "success",
      });
      if (hasAuthoritativeFieldBoundaries(field)) {
        void refreshFieldSoilEnrichment(farmId, response.data.id, user).catch(() => null);
      }
      clearFormDraft(draftKey);
      navigate(`/farm/${farmId}/field/${response.data.id}`);
    } catch (error) {
      if (isBoundaryOverlapError(error)) {
        setBoundaryValidation(normalizeBoundaryValidationError(error));
        setSaveError("These boundaries overlap another saved field. Adjust the boundary or its exclusions and retry.");
        return;
      }
      if (!error.response && error.message) { setSaveError(error.message); return; }
      if (error.response?.status === 409 && typeof error.response.data?.detail === "string" && /draft|record changed|assessment changed|context changed/i.test(error.response.data.detail)) { setSaveError(error.response.data.detail); return; }
      if (isConflictError(error)) {
        setSaveError(
          duplicateNameMessage(error, {
            resourceLabel: "field",
            name: field.name,
            scope: "on this farm",
          })
        );
        return;
      }

      setBoundaryValidation(normalizeBoundaryValidationError(error));
    } finally {
      setIsSaving(false);
    }
  }

  const isFieldDirty = useMemo(
    () =>
      hasComparableFormChanges(
        buildFieldPayload(field),
        buildFieldPayload(savedField)
      ),
    [field, savedField]
  );

  const isSaveDisabled =
    isSaving ||
    !isFieldDirty ||
    (field.has_boundaries &&
      (boundaryValidation.status === "loading" ||
        boundaryValidation.status === "invalid"));

  if (loadedDraftKey !== draftKey) return <Page title={isEditing ? "Edit Field" : "New Field"}>
    <div role={saveError ? "alert" : "status"}>
      {saveError || "Loading field form…"}
      {saveError ? <Button onClick={() => window.location.reload()}>Reload field form</Button> : null}
    </div>
  </Page>;

  return (
    <Page
      title={
        isEditing
          ? `${displayedField?.name || "Field"} on ${farm?.name || "Farm"}`
          : `New Field on ${farm?.name || ""}`
      }
      headerBorderColor={"border-[#34a853]"}
      headerVariant="hybrid"
      contentWidth="map"
      headerActions={
        isEditing ? (
          <VersionHistoryControl
            activeSummary={history.activeSummary}
            canViewNewer={history.canViewNewer}
            canViewOlder={history.canViewOlder}
            detailState={history.detailState}
            dialogOpen={history.dialogOpen}
            errorMessage={history.errorMessage}
            onClose={history.closeDialog}
            onOpen={history.openDialog}
            onRestore={history.restoreSelectedVersion}
            onSelectVersion={history.selectVersion}
            onShowCurrent={history.showCurrentVersion}
            onViewNewer={
              history.canViewNewer
                ? () => history.selectAdjacentVersion(-1)
                : undefined
            }
            onViewOlder={
              history.canViewOlder
                ? () => history.selectAdjacentVersion(1)
                : undefined
            }
            resourceLabel="Field"
            restoreState={history.restoreState}
            summaries={history.summaries}
            summaryState={history.summaryState}
          />
        ) : null
      }
      introContent={
        <p>
          Confirm the field details in the left rail and use the map to search,
          inspect, and draw boundaries. This keeps the field record grounded in the
          same mapped geography used elsewhere in the grower workflow.
        </p>
      }
      padding={"p-[0px]"}
    >
      <div className="flex h-full w-full flex-col">
        <div className="px-4 pb-6 pt-5 md:px-6 md:pb-8">
          <DraftSaveStatus state={autosave} />
          {recoverableDraft ? <Alert severity="info" className="mb-4" action={<div className="flex gap-2">
            <Button onClick={() => { autosave.selectRecovery(recoverableDraft); setField(recoverableDraft.value); setRecoverableDraft(null); }}>Restore draft</Button>
            <Button onClick={() => { autosave.discard(recoverableDraft); setRecoverableDraft(null); }}>Discard draft</Button>
          </div>}>
            Recoverable field edits are available. Saving applies them to the field.
            <DraftRecoveryChoices draft={recoverableDraft} onChoose={setRecoverableDraft} />
            {recoverableDraft.versionId !== savedField.current_version_id ? " The saved field has changed; review the restored values before saving." : ""}
          </Alert> : null}
          <HistoryViewingBanner
            onOpenHistory={history.openDialog}
            onRestore={history.restoreSelectedVersion}
            onShowCurrent={history.showCurrentVersion}
            onViewNewer={
              history.canViewNewer
                ? () => history.selectAdjacentVersion(-1)
                : undefined
            }
            onViewOlder={
              history.canViewOlder
                ? () => history.selectAdjacentVersion(1)
                : undefined
            }
            restoreDisabled={history.restoreState === "saving"}
            restoring={history.restoreState === "saving"}
            summary={history.activeSummary}
          />
          <div
            className={`overflow-hidden rounded-[8px] border border-slate-200 bg-white shadow-[0_24px_60px_rgba(18,28,45,0.08)] ${
              isViewingHistoricalVersion ? "opacity-85" : ""
            }`}
          >
            <div className="flex items-center justify-between border-b border-slate-200 bg-[#fcfcfa] px-6 py-5">
              <div>
                <p className="text-[12px] font-bold uppercase tracking-[0.18em] text-slate-400">
                  Field Setup
                </p>
                <h2 className="mt-2 text-[28px] font-black text-slate-900">
                  Capture field details and location
                </h2>
              </div>
              <div className="hidden text-right text-sm text-slate-500 lg:block">
                {displayedField?.field_boundaries_size
                  ? `${formatAcres(
                      displayedField.field_boundaries_size
                    )} calculated acres`
                  : "Coordinates or boundaries"}
              </div>
            </div>

            <div className="flex h-full w-full flex-col md:min-h-[820px] md:flex-row">
              <div
                className={`relative flex w-full flex-col justify-end border-b border-slate-200 bg-[#fcfcfa] md:w-[380px] md:min-w-[380px] md:border-b-0 md:border-r ${
                  isViewingHistoricalVersion ? "pointer-events-none" : ""
                }`}
              >
                <div
                  style={{
                    height: "calc(100% - 152px)",
                  }}
                  className="absolute left-0 top-0 flex w-full flex-col overflow-auto px-5 py-5"
                >
            <p className="mb-4 text-sm text-slate-600">You can save a draft with just a field name. Analysis needs acreage and a valid location; official soil estimates need a boundary. Complete those inputs when you want to use the operation.</p>
            <FormTextField
              disabled={!farm}
              errorFound={errorFields.name}
              fieldState={displayedField?.name}
              fieldLabel={"Field Name"}
              modalTitle={"Field Name"}
              onChange={(t) => handleStateChange(t, "name")}
              modalDescription={
                "Enter the name of your field. Use a name you will recognize."
              }
            />
            <FormTextField
              disabled={!farm}
              errorFound={errorFields.fieldSize}
              fieldState={displayedField?.field_size}
              fieldLabel={"Field Size [Acres]"}
              modalTitle={"Field Size"}
              onChange={(t) => handleStateChange(t, "field_size")}
              isNumber={true}
              formatValueWhenBlurred={formatAcresForInput}
              modalDescription={
                "Enter the size of your field. Please use Acres as a unit of measurement, since the system will expects this unit to compute the analysis."
              }
            />
            <FormTextField
              disabled={!farm || displayedBoundariesAreAuthoritative}
              errorFound={errorFields.fieldAddress}
              fieldState={displayedField?.field_address}
              fieldLabel={"Field Address Look Up"}
              helperText={
                displayedBoundariesAreAuthoritative
                  ? "Field boundaries control the map location. Clear boundaries on the map to use address lookup."
                  : "Use a city, postal code, or address to move the map before drawing boundaries."
              }
              modalTitle={"Field Address"}
              modalDescription={
                "Use a city, postal code, or address to move the map before drawing. Once boundaries are provided, clear them before using lookup again."
              }
              onChange={(t) => handleStateChange(t, "field_address")}
              onFocus={() => {
                setIsTypingAddress(true);
              }}
              onBlur={handleAddressChange}
            />
            <p className="mb-3 text-xs leading-5 text-slate-600">
              Draw a boundary or enter field coordinates for Holos. Boundaries
              are needed for official soil enrichment and crop-history lookup.
            </p>
            <FormTextField
              disabled={!farm || displayedBoundariesAreAuthoritative}
              errorFound={errorFields.latitude}
              fieldState={displayedField?.latitude ?? ""}
              fieldLabel="Latitude"
              isNumber={true}
              onChange={(value) => handleStateChange(value, "latitude")}
              helperText={errorFields.latitude ? "Enter a latitude from -90 to 90." : "Decimal degrees; boundaries take precedence"}
            />
            <FormTextField
              disabled={!farm || displayedBoundariesAreAuthoritative}
              errorFound={errorFields.longitude}
              fieldState={displayedField?.longitude ?? ""}
              fieldLabel="Longitude"
              isNumber={true}
              onChange={(value) => handleStateChange(value, "longitude")}
              helperText={errorFields.longitude ? "Enter a longitude from -180 to 180." : "Decimal degrees; use a negative value for Canada"}
            />
            {mapSearchError ? (
              <p className="-mt-2 mb-4 text-xs font-semibold text-red-700">
                {mapSearchError}
              </p>
            ) : null}
            <div>
              <FormTextField
                errorFound={false}
                fieldState={displayedField?.field_boundaries_size}
                onChange={() => {}}
                inputProps={{ readOnly: true }}
                formatValueWhenBlurred={formatAcresForInput}
                fieldLabel={"Calculated Acres from Field Boundaries"}
                modalTitle={"Calculated Field Acres "}
                modalDescription={
                  "This value represents the approximate size of the field boundaries provided in the map drawing. It's calculated by measuring the area of the polygon created by the vertices added onto the map."
                }
              />
            </div>

            <BooleanQuestion
              fieldValue={displayedField?.has_boundaries}
              fieldLabel={"Field Boundaries Provided?"}
              modalTitle={"Field Boundaries"}
              modalDescription={
                "Providing your field boundaries allows us to have the a list of the surrounding coordinates of your field, this allows us to dynamically calculate your field's size, and it is very useful for documentation purposes."
              }
            />
            <FieldBoundaryValidationNotice validationState={boundaryValidation} />
            <FieldBoundaryImport inputRef={boundaryImportInputRef} toolbarReady={boundaryToolbarReady} disabled={isViewingHistoricalVersion || isSaving}
              onImport={(changes) => setField((current) => ({ ...current, ...changes }))} />
            {displayedField?.field_boundaries_size > 0 && !isViewingHistoricalVersion ? <Button onClick={() => handleStateChange(displayedField.field_boundaries_size, "field_size")}>Use boundary acres as field size</Button> : null}
            <FieldSoilEstimatePanel
              reason={displayedField?.boundaries?.length >= 3 ? soilEnrichmentState?.reason : "field_boundary_required"}
              canRefresh={
                isEditing &&
                !isViewingHistoricalVersion &&
                Boolean(field.has_boundaries && field.boundaries?.length >= 3)
              }
              compact
              enrichment={
                coordinateSoilResolution || soilEnrichmentState?.enrichment || null
              }
              error={soilEnrichmentError}
              loading={soilEnrichmentLoading}
              onRefresh={handleRefreshSoilEnrichment}
            />
            {saveError ? (
              <p className="mb-4 text-sm font-semibold text-[#c62828]">
                {saveError}
              </p>
            ) : null}
            <Divider sx={{ marginBottom: 3 }} />
            <MeasuredSoilHorizonInput value={displayedField?.topsoil_horizon_thickness_cm} onChange={value => handleStateChange(value, "topsoil_horizon_thickness_cm")} disabled={!farm || isViewingHistoricalVersion} error={errorFields.topsoilHorizonThickness} />
            <p className="mb-4 text-xs leading-5 text-slate-500">
              These soil-description fields are optional records and are not
              required for the current Holos output. Boundary enrichment can
              fill blank values; your selections are preserved.
            </p>
            <FormSelectField
              errorFound={errorFields.surfaceForm}
              valuesArray={referenceData.surface_forms || []}
              onChange={(e) => handleStateChange(e.target.value, "surface_form")}
              fieldLabel={"Surface Form"}
              fieldState={displayedField?.surface_form}
              helperText={""}
              modalTitle={"Surface Form"}
              modalDescription={
                "Refers to parallel and subparallel steep-sided, and narrow ravines that have developed from fluvial erosion on landscapes with strong to steep slopes."
              }
            />
            <FormSelectField
              errorFound={errorFields.slopeClass}
              valuesArray={referenceData.slope_classes || []}
              fieldLabel={"Slope Class"}
              fieldState={displayedField?.slope_class}
              onChange={(e) => handleStateChange(e.target.value, "slope_class")}
              helperText={""}
              modalTitle={"Slope Class"}
              modalDescription={
                "Slope percent (%) is the steepness of a sloping area in your field. Slope percent impacts erosion on your field. We recommend determining the percent slope using a clinometer measuring the dominant slope acrossthe field. If do not have access to a clinometer, the information in the following table maybe be useful. First select the approximate field size (acres) and/or field width/length (feet), then identify the approximate elevation change (feet) over the field and the correlating slope percent."
              }
            />
            <FormSelectField
              errorFound={errorFields.soilType}
              valuesArray={referenceData.soil_types || []}
              fieldLabel={"Soil Type"}
              fieldState={displayedField?.soil_type}
              onChange={(e) => handleStateChange(e.target.value, "soil_type")}
              helperText={""}
              modalTitle={"Soil Type"}
              modalDescription={"Some helpful description."}
            />
            <FormSelectField
              errorFound={errorFields.surfaceSoilTexture}
              valuesArray={referenceData.surface_soil_textures || []}
              fieldLabel={"Surface Soil Texture"}
              fieldState={displayedField?.surface_soil_texture}
              onChange={(e) =>
                handleStateChange(e.target.value, "surface_soil_texture")
              }
              helperText={""}
              modalTitle={"Surface Soil Texture"}
              modalDescription={
                "Dominant surface soil texture class for the field boundary."
              }
            />
          </div>

                <div className="flex w-full flex-col gap-3 border-t border-slate-200 bg-white/92 px-5 py-4">
                  <MainButton
                    text={
                      boundaryValidation.status === "loading"
                        ? "Checking Boundaries..."
                        : isSaving
                          ? "Saving Field..."
                        : isEditing
                          ? "Save Changes"
                          : "Save Field"
                    }
                    onClick={handleSaveField}
                    disabled={isSaveDisabled}
                  />
                  <MainButton
                    text={"Cancel"}
                    onClick={() =>
                      navigate(
                        isEditing
                          ? `/farm/${displayedField?.farm_id || field.farm_id}/field/${fieldId}`
                          : `/farm/${farmId}`
                      )
                    }
                    secondary={true}
                  />
                </div>
              </div>

              <div className="hidden w-full bg-[#eef3f5] p-4 md:block">
                <div className="h-full overflow-hidden rounded-[8px] border border-slate-200 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.08)]">
                  <MapComponent
                    key={`${farmId}:${fieldId || "new"}`}
                    fieldKey={`${farmId}:${fieldId || "new"}`}
                    onImportBoundary={() => boundaryImportInputRef.current?.click()}
                    onBoundaryToolbarReady={setBoundaryToolbarReady}
                    importDisabled={isViewingHistoricalVersion || isSaving}
                    GOOGLE_APIKEY={config?.GOOGLE_APIKEY}
                    address={addressQuery ? addressQuery : "Canada"}
                    drawFieldBoundaries={true}
                    isTypingAddress={isTypingAddress}
                    searchLocation={mapSearchLocation}
                    fieldState={displayedField}
                    setFieldState={setField}
                    onSaveBoundaries={handleSaveBoundaries}
                    savingBoundaries={isSaving || soilEnrichmentLoading}
                    referenceFarms={referenceFarms}
                    overlapConflicts={boundaryValidation.conflicts}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <WarningMessageModal
          title={"Check the field location"}
          description={`The mapped field appears outside ${buildCoordinateRegionDescription(
            farm?.province
          )}. Check the coordinates or redraw the boundary before saving.`}
          open={boundaryLocationWarningOpen}
          handleClose={setBoundaryLocationWarningOpen}
        />
      </div>
    </Page>
  );
}
